"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Ticket, TicketStatus, TicketPriority } from "@/types/models/ticket";
import {
  User,
  Building,
  Tag,
  Calendar,
  Clock,
  AlertTriangle,
  Flag,
  MessageCircle,
  Users,
  Edit3,
} from "lucide-react";
import { format } from "date-fns";
import { useAppDispatch } from "@/store/hooks";
import { updateTicketRequest } from "@/store/slices/ticket.slice";

interface TicketPropertiesPanelProps {
  ticket: Ticket;
  onUpdate?: (ticket: Ticket) => void;
}

const getPriorityColor = (priority: TicketPriority) => {
  switch (priority) {
    case "low":
      return "bg-green-100 text-green-800 border-green-200";
    case "normal":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "high":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "urgent":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getStatusColor = (status: TicketStatus) => {
  switch (status) {
    case "open":
      return "bg-blue-500/20 text-blue-600";
    case "in_progress":
      return "bg-orange-500/20 text-orange-600";
    case "pending_customer":
      return "bg-yellow-500/20 text-yellow-600";
    case "resolved":
      return "bg-green-500/20 text-green-600";
    case "closed":
      return "bg-gray-500/20 text-gray-600";
    default:
      return "bg-gray-500/20 text-gray-600";
  }
};

export const TicketPropertiesPanel: React.FC<TicketPropertiesPanelProps> = ({
  ticket,
}) => {
  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState<string | null>(null);

  const handlePropertyUpdate = (field: string, value: any) => {
    dispatch(
      updateTicketRequest({
        ticketId: ticket.id,
        ticket: { [field]: value },
      }),
    );
    setEditMode(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not set";
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  const formatParticipantType = (type?: string) => {
    if (!type) return "Unknown";
    return type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const ticketType = ticket.type || ticket.metadata?.type || "general";
  const normalizedTicketType = String(ticketType).toLowerCase();

  const PropertyRow = ({
    icon,
    label,
    value,
    field,
    options,
    renderValue,
  }: {
    icon: React.ReactNode;
    label: string;
    value: any;
    field?: string;
    options?: { label: string; value: string }[];
    renderValue?: () => React.ReactNode;
  }) => {
    const isEditing = editMode === field;

    return (
      <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0 gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="text-muted-foreground">{icon}</div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </span>
            <div className="mt-0.5">
              {isEditing && options ? (
                <Select
                  value={value}
                  onValueChange={(newValue) =>
                    handlePropertyUpdate(field!, newValue)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : renderValue ? (
                renderValue()
              ) : (
                <span className="text-sm truncate">{value || "Not set"}</span>
              )}
            </div>
          </div>
        </div>
        {field && options && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setEditMode(isEditing ? null : field)}
            className="h-7 w-7 shrink-0"
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="w-72 space-y-3">
      {/* Ticket Details Card */}
      <Card className="shadow-sm">
        <CardHeader className="px-4 py-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Flag className="h-4 w-4" />
            Ticket Details
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0 space-y-0">
          <PropertyRow
            icon={<AlertTriangle className="h-4 w-4" />}
            label="Priority"
            value={ticket.priority}
            field="priority"
            options={[
              { label: "Low", value: "low" },
              { label: "Medium", value: "normal" },
              { label: "High", value: "high" },
              { label: "Urgent", value: "urgent" },
            ]}
            renderValue={() => (
              <Badge
                variant="outline"
                className={`text-xs border ${getPriorityColor(ticket.priority)}`}
              >
                {ticket.priority}
              </Badge>
            )}
          />

          <PropertyRow
            icon={<Flag className="h-4 w-4" />}
            label="Status"
            value={ticket.status}
            field="status"
            options={[
              { label: "Open", value: "open" },
              { label: "In Progress", value: "in_progress" },
              { label: "Pending Customer", value: "pending_customer" },
              { label: "Resolved", value: "resolved" },
              { label: "Closed", value: "closed" },
            ]}
            renderValue={() => (
              <Badge
                variant="secondary"
                className={`rounded-full px-3 py-1 ${getStatusColor(ticket.status)}`}
              >
                {ticket.status.replace(/_/g, " ")}
              </Badge>
            )}
          />

          <PropertyRow
            icon={<MessageCircle className="h-4 w-4" />}
            label="Subject"
            value={ticket.subject || ticket.title || "Untitled"}
          />

          <PropertyRow
            icon={<Tag className="h-4 w-4" />}
            label="Type"
            value={normalizedTicketType}
            field="type"
            options={[
              { label: "Technical", value: "technical" },
              { label: "General", value: "general" },
              { label: "Website", value: "website" },
              { label: "Products", value: "products" },
              { label: "Orders", value: "orders" },
              { label: "Payments", value: "payments" },
              { label: "Shipping", value: "shipping" },
              { label: "Billing", value: "billing" },
              { label: "Account", value: "account" },
              { label: "Other", value: "other" },
            ]}
            renderValue={() => (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {normalizedTicketType}
              </Badge>
            )}
          />

          <PropertyRow
            icon={<User className="h-4 w-4" />}
            label="Assigned To"
            value={ticket.assignedTo}
            renderValue={() => (
              <div className="flex items-center gap-2">
                <Avatar
                  name={ticket.assignedToName || ""}
                  className="h-5 w-5"
                />
                <span className="text-sm truncate">
                  {ticket.assignedToName || "Unassigned"}
                </span>
              </div>
            )}
          />

          <PropertyRow
            icon={<Building className="h-4 w-4" />}
            label="Store"
            value={ticket.storeName}
          />

          <PropertyRow
            icon={<User className="h-4 w-4" />}
            label="Seller"
            value={ticket.sellerName || "N/A"}
          />

          <PropertyRow
            icon={<Calendar className="h-4 w-4" />}
            label="Due Date"
            value={formatDate(ticket.dueDate)}
          />

          <PropertyRow
            icon={<Clock className="h-4 w-4" />}
            label="Start Date"
            value={formatDate(ticket.startDate || ticket.created_at)}
          />

          <PropertyRow
            icon={<User className="h-4 w-4" />}
            label="Created By"
            value={ticket.createdById}
            renderValue={() => (
              <div className="flex items-center gap-2">
                <Avatar name={ticket.createdByType} className="h-6 w-6" />
                <div>
                  <div className="text-sm font-medium">
                    {formatParticipantType(ticket.createdByType)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {ticket.createdById}
                  </div>
                </div>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Participants Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Participants ({ticket.participants?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {ticket.participants?.map((participant) => (
              <div key={participant.id} className="flex items-center gap-3">
                <Avatar
                  name={participant.participantName || participant.userId}
                  className="h-8 w-8"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {participant.participantName || participant.userId}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatParticipantType(participant.userType)}
                  </div>
                  {participant.participantEmail && (
                    <div className="text-xs text-muted-foreground truncate">
                      {participant.participantEmail}
                    </div>
                  )}
                </div>
                <Badge
                  variant="outline"
                  className={`text-xs shrink-0 ${
                    participant.role === "requester"
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : participant.role === "assignee"
                        ? "bg-purple-100 text-purple-800 border-purple-200"
                        : "bg-gray-100 text-gray-800 border-gray-200"
                  }`}
                >
                  {participant.role}
                </Badge>
              </div>
            ))}

            {(!ticket.participants || ticket.participants.length === 0) && (
              <div className="text-sm text-muted-foreground text-center py-4">
                No participants yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tags Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Tags ({ticket.tags?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {ticket.tags?.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                className="text-xs"
                style={{
                  backgroundColor: `${tag.color}20`,
                  borderColor: tag.color,
                  color: tag.color,
                }}
              >
                {tag.name}
              </Badge>
            ))}

            {(!ticket.tags || ticket.tags.length === 0) && (
              <div className="text-sm text-muted-foreground">No tags</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Related Items Card */}
      {(ticket.relatedOrderId || ticket.relatedProductId) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Related Items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ticket.relatedOrderId && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Order:</span>
                <Badge variant="outline" className="text-xs">
                  #{ticket.relatedOrderId.slice(-8)}
                </Badge>
              </div>
            )}
            {ticket.relatedProductId && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Product:</span>
                <Badge variant="outline" className="text-xs">
                  {ticket.relatedProductId.slice(-8)}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
